/**
 * 68.10 — fluid squad membership (DIRECTION §0.7, lane D's note, the owner's four answers, Oct 10).
 *
 * A squad is an identity (its number, its orders, its formation); its membership is the list `unitIds` — in **join order**,
 * so the senior member is the first: the leader is the first *living* member (when it falls the next takes over on the same
 * tick). Membership changes only by these rules, in this order, on state alone, once per sight period (Match.stepAction):
 *
 *   - **Separation:** a member farther than SQUAD_RULES.separatePeople from its leader (or on another planet) for
 *     `separateSeconds` is separated — it fights alone (no squad order reaches it: `separatedAt`) but stays listed; after
 *     `graceSeconds` more it is a **stray** and leaves. Back within reach before then, it is simply a member again.
 *   - **Absorption:** a stray (a living unit in no squad) within `absorbPeople` of a living member of a friendly squad with
 *     room joins it — room is the battle's squad size + 1 (the owner: "yes, by one"); ties go to the nearest leader, then
 *     the lowest squad number. The player's squad takes strays the same way.
 *   - **Merging:** two squads of one living member each, within `absorbPeople`, merge into the lower number.
 *   - **Respawns** (`squadsOnRespawn`, after a wave): a respawned unit joins the squad with room whose leader is nearest its
 *     spawn within `respawnPeople`; else its own squad, if that has living members; else it re-forms a spent squad (the
 *     team's lowest-numbered one with nobody alive) under its old number (the owner: "yes, keep the number").
 *     Decision 139 (with squad leaders on): a squad's designated leader (`MatchSquad.leader`) always rejoins its own squad.
 *
 * Each change is a `joined` event (unit, from, to, reason). Distances are in person heights (the owner). Pure.
 */
import type { GameState, MatchSquad, PlayerState } from '../types';
import { PERSON_BODY_HEIGHT, PHYS_DT } from '../constants';
import { dacos } from './dmath';
import { personSetOf } from './scaleProfile';

export const SQUAD_RULES = { separatePeople: 12, separateSeconds: 5, graceSeconds: 30, absorbPeople: 4, respawnPeople: 20 } as const;

export type MembershipReason = 'stray' | 'absorbed' | 'merged' | 'respawn' | 'returned' | 'reformed';
export interface MembershipEvent { kind: 'joined'; unitId: number; from: number | null; to: number | null; reason: MembershipReason }

/** The squad's leader: its first living member (join order = seniority), or null when none lives. */
export function squadLeader(state: Pick<GameState, 'players'>, squad: MatchSquad): PlayerState | null {
  for (const id of squad.unitIds) { const p = state.players.find(q => q.id === id); if (p?.alive) return p; }
  return null;
}

/**
 * The squad's leader now (decision 139: "no leader promotion system at the moment, just respawn of that leader"): its
 * designated unit while that is alive and in the squad, else none — the unit whose brain sets the squad's step
 * (ai/squadLead.ts). Not `squadLeader` above (the first living member, the membership rules' seniority).
 */
export function squadLeadOf(state: Pick<GameState, 'players'>, squad: Pick<MatchSquad, 'unitIds' | 'leader'>): PlayerState | null {
  const id = squad.leader ?? squad.unitIds[0];
  if (id === undefined || !squad.unitIds.includes(id)) return null;
  const u = state.players.find(p => p.id === id);
  return u && u.alive ? u : null;
}

/** Is this unit separated from its squad at `tick` (apart for SQUAD_RULES.separateSeconds or more)? A separated unit fights alone. */
export function separatedAt(state: Pick<GameState, 'squadApart'>, unitId: number, tick: number): boolean {
  const since = state.squadApart?.[unitId];
  return since !== undefined && (tick - since) * PHYS_DT >= SQUAD_RULES.separateSeconds;
}

function livingCount(state: Pick<GameState, 'players'>, s: MatchSquad): number {
  let n = 0;
  for (const id of s.unitIds) if (state.players.find(p => p.id === id)?.alive) n++;
  return n;
}

/** The distance between two units in person heights, along the planet (Infinity across planets). */
function apartPeople(state: Pick<GameState, 'planets' | 'config'>, a: PlayerState, b: PlayerState): number {
  if (a.planetIndex !== b.planetIndex) return Infinity;
  const planet = state.planets[a.planetIndex];
  if (!planet) return Infinity;
  const d = Math.max(-1, Math.min(1, a.surfaceDir.x * b.surfaceDir.x + a.surfaceDir.y * b.surfaceDir.y + a.surfaceDir.z * b.surfaceDir.z));
  return (dacos(d) * planet.radius) / (PERSON_BODY_HEIGHT * personSetOf(state).k);
}

const withMember = (s: MatchSquad, id: number): MatchSquad => ({ ...s, unitIds: [...s.unitIds.filter(x => x !== id), id] });
const withoutMember = (s: MatchSquad, id: number): MatchSquad => ({ ...s, unitIds: s.unitIds.filter(x => x !== id) });

/** Move a unit from whatever squad it is in to `to` (or out of every squad with null), keeping join order (it joins at the back). */
function move(squads: MatchSquad[], unitId: number, to: number | null): MatchSquad[] {
  return squads.map(s => (s.id === to ? withMember(s, unitId) : s.unitIds.includes(unitId) ? withoutMember(s, unitId) : s));
}

/**
 * One membership pass (once per sight period): separation and strays, absorption, merging. `size` is the battle's squad
 * size (room is size + 1). Returns the new state and the changes, in a fixed order (units by id, squads by id).
 */
export function stepSquadMembership(state: GameState, tick: number, size: number): { state: GameState; events: MembershipEvent[] } {
  if (!state.squads?.length) return { state, events: [] };
  let squads = state.squads;
  const apart: Record<number, number> = { ...(state.squadApart ?? {}) };
  const events: MembershipEvent[] = [];
  const unit = (id: number) => state.players.find(p => p.id === id);

  // 1. separation and strays
  for (const s of [...squads].sort((a, b) => a.id - b.id)) {
    const leader = squadLeader(state, s);
    for (const id of [...s.unitIds].sort((a, b) => a - b)) {
      const u = unit(id);
      if (!u?.alive || !leader || u.id === leader.id) { delete apart[id]; continue; }
      if (apartPeople(state, u, leader) <= SQUAD_RULES.separatePeople) { delete apart[id]; continue; }
      apart[id] ??= tick;
      if ((tick - apart[id]) * PHYS_DT >= SQUAD_RULES.separateSeconds + SQUAD_RULES.graceSeconds) {
        squads = move(squads, id, null);
        delete apart[id];
        events.push({ kind: 'joined', unitId: id, from: s.id, to: null, reason: 'stray' });
      }
    }
  }

  // 2. absorption: strays (living, in no squad) near a living member of a friendly squad with room
  const inSquad = (id: number) => squads.some(s => s.unitIds.includes(id));
  for (const u of [...state.players].sort((a, b) => a.id - b.id)) {
    if (!u.alive || inSquad(u.id)) continue;
    let best: { s: MatchSquad; lead: number } | null = null;
    for (const s of [...squads].sort((a, b) => a.id - b.id)) {
      if (s.team !== u.teamIndex || livingCount(state, s) >= size + 1) continue;
      const near = s.unitIds.some(id => { const m = unit(id); return !!m?.alive && apartPeople(state, u, m) <= SQUAD_RULES.absorbPeople; });
      if (!near) continue;
      const leader = squadLeader(state, s);
      const lead = leader ? apartPeople(state, u, leader) : Infinity;
      if (!best || lead < best.lead) best = { s, lead };
    }
    if (best) {
      squads = move(squads, u.id, best.s.id);
      events.push({ kind: 'joined', unitId: u.id, from: null, to: best.s.id, reason: 'absorbed' });
    }
  }

  // 3. merging: two squads of one living member each, near each other, into the lower number
  for (const a of [...squads].sort((x, y) => x.id - y.id)) {
    const cur = squads.find(s => s.id === a.id)!;
    if (livingCount(state, cur) !== 1) continue;
    const ua = squadLeader(state, cur)!;
    for (const b of [...squads].sort((x, y) => x.id - y.id)) {
      if (b.id <= cur.id || b.team !== cur.team) continue;
      const curB = squads.find(s => s.id === b.id)!;
      if (livingCount(state, curB) !== 1) continue;
      const ub = squadLeader(state, curB)!;
      if (apartPeople(state, ua, ub) > SQUAD_RULES.absorbPeople) continue;
      squads = move(squads, ub.id, cur.id);
      delete apart[ub.id];
      events.push({ kind: 'joined', unitId: ub.id, from: curB.id, to: cur.id, reason: 'merged' });
      break;
    }
  }

  const changed = squads !== state.squads;
  const apartSame = JSON.stringify(apart) === JSON.stringify(state.squadApart ?? {});
  if (!changed && apartSame) return { state, events };
  return { state: { ...state, squads, squadApart: Object.keys(apart).length ? apart : undefined }, events };
}

/** After a respawn wave: each respawned unit (by id) finds its squad — the nearest leader's with room near the spawn, its own, or a spent one re-formed. */
export function squadsOnRespawn(state: GameState, unitIds: readonly number[], size: number): { state: GameState; events: MembershipEvent[] } {
  if (!state.squads?.length || !unitIds.length) return { state, events: [] };
  let squads = state.squads;
  const events: MembershipEvent[] = [];
  const apart = { ...(state.squadApart ?? {}) };
  for (const id of [...unitIds].sort((a, b) => a - b)) {
    const u = state.players.find(p => p.id === id);
    if (!u?.alive) continue;
    delete apart[id];
    const own = squads.find(s => s.unitIds.includes(id)) ?? null;
    // decision 139 (with squad leaders on, 68.9): a squad's designated leader is never replaced and leads again when it
    // respawns — it rejoins its own squad, ahead of the nearest-with-room rule (everyone else as before)
    if (own && own.leader === id) continue;
    let best: { s: MatchSquad; d: number } | null = null;
    for (const s of [...squads].sort((a, b) => a.id - b.id)) {
      if (s.team !== u.teamIndex || s.id === own?.id) continue;
      const leader = squadLeader(state, s);
      if (!leader || livingCount(state, s) >= size + 1) continue;
      const d = apartPeople(state, u, leader);
      if (d <= SQUAD_RULES.respawnPeople && (!best || d < best.d)) best = { s, d };
    }
    // its own squad's living members count without itself (it was dead until now)
    const ownLiving = own ? own.unitIds.filter(x => x !== id && state.players.find(p => p.id === x)?.alive).length : 0;
    const ownLeader = own && ownLiving > 0 ? squadLeader({ players: state.players.filter(p => p.id !== id) }, own) : null;
    const ownNear = ownLeader ? apartPeople(state, u, ownLeader) : Infinity;
    if (best && !(ownLeader && ownNear <= best.d)) {
      squads = move(squads, id, best.s.id);
      events.push({ kind: 'joined', unitId: id, from: own?.id ?? null, to: best.s.id, reason: 'respawn' });
    } else if (own && ownLiving > 0) {
      continue;   // back to its own squad (already listed there)
    } else {
      // re-form a spent squad: the team's lowest-numbered squad with nobody alive (its own, if spent), under its number
      const spent = [...squads].sort((a, b) => a.id - b.id).find(s => s.team === u.teamIndex && s.unitIds.every(x => x === id || !state.players.find(p => p.id === x)?.alive));
      const to = spent?.id ?? own?.id ?? null;
      if (to === null || to === own?.id) {
        if (own) { squads = squads.map(s => (s.id === own.id ? { ...s, unitIds: [id, ...s.unitIds.filter(x => x !== id)] } : s)); events.push({ kind: 'joined', unitId: id, from: own.id, to: own.id, reason: 'reformed' }); }
        continue;
      }
      squads = move(squads, id, to);
      squads = squads.map(s => (s.id === to ? { ...s, unitIds: [id, ...s.unitIds.filter(x => x !== id)] } : s));   // it leads the re-formed squad
      events.push({ kind: 'joined', unitId: id, from: own?.id ?? null, to, reason: 'reformed' });
    }
  }
  return { state: { ...state, squads, squadApart: Object.keys(apart).length ? apart : undefined }, events };
}
